import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack, Tooltip } from '@mui/material';

import { QueryKey } from '@tanstack/react-query';

import { ReactComponent as LinkExternalIcon } from '@/assets/icons/link-external.svg';
import { ReactComponent as RefreshIcon } from '@/assets/icons/refresh-cw-icon.svg';
import { ReactComponent as UsersIcon } from '@/assets/icons/users-outline-icon.svg';
import { formatDateTime } from '@/shared/lib/format';

import { useOrderDriveMutations } from '../api/useArtworkDriveMutations';
import { useDriveUpload } from '../api/useDriveUpload';
import { DriveFolderRef, acceptsUploads } from '../model/driveFolders';
import { safeDriveLink } from '../model/driveLinks';
import { DriveFolderSummary } from '../model/types';
import DriveFolderMenu from './DriveFolderMenu';
import type { DriveFolderMenuAction } from './DriveFolderMenu';
import DriveFolderPill from './DriveFolderPill';
import DriveAccessDialog from './dialogs/DriveAccessDialog';
import GoogleSignInDialog from './dialogs/GoogleSignInDialog';
import { stopRowClick } from './stopRowClick';

const iconSx = { color: 'grey.600', '&:hover': { color: 'accent.main' } } as const;

interface Props {
  orderId: string;
  folder: DriveFolderRef;
  summary: DriveFolderSummary | undefined;
  /** What the folder is, for the Picker's and the access panel's titles. */
  title: string;
  canUpload?: boolean;
  canManage?: boolean;
  alsoInvalidate?: QueryKey[];
  extraActions?: DriveFolderMenuAction[];
  /**
   * `icons` trades the counts pill for an open-in-Drive icon beside a ⋮ menu, for a folder tree that already
   * names and counts each folder itself.
   */
  presentation?: 'pill' | 'icons';
}

/**
 * One Drive folder: a button reading "N files • M links", with "Last synced" on hover, that opens "Open in Drive" and
 * "Upload" above the folder's other actions. Every action stops the click from reaching the grid row underneath.
 */
const DriveFolderStatus: FC<Props> = ({
  orderId,
  folder,
  summary,
  title,
  canUpload = false,
  canManage = false,
  alsoInvalidate,
  extraActions = [],
  presentation = 'pill',
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const { upload, uploading, preload, signInNeeded, continueWithGoogle, dismissSignIn } =
    useDriveUpload(orderId, alsoInvalidate);
  const { refresh } = useOrderDriveMutations(orderId, alsoInvalidate);

  const link = safeDriveLink(summary?.folderLink);
  const lastSynced = summary?.lastSyncedAt
    ? t('artwork_drive.last_synced', { time: formatDateTime(summary.lastSyncedAt) })
    : t('artwork_drive.never_synced');
  const showUpload = canUpload && acceptsUploads(folder) && Boolean(link);
  const menuActions: DriveFolderMenuAction[] = [
    ...(canManage && acceptsUploads(folder)
      ? [
          {
            key: 'refresh',
            label: t('artwork_drive.refresh'),
            icon: <RefreshIcon />,
            onClick: () => refresh.mutate(folder),
          },
        ]
      : []),
    ...(canManage && link
      ? [
          {
            key: 'access',
            label: t('artwork_drive.manage_access'),
            icon: <UsersIcon />,
            onClick: () => setAccessOpen(true),
          },
        ]
      : []),
    ...extraActions,
  ];
  const opensMenu = Boolean(link) || menuActions.length > 0;
  const closeMenu = () => setMenuAnchor(null);
  const openMenu = (event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget);

  return (
    <Stack
      direction="row"
      alignItems="center"
      onClick={stopRowClick}
      onMouseDown={stopRowClick}
      data-testid={`drive-folder-${folder.kind}`}
    >
      {presentation === 'icons' ? (
        <>
          {link ? (
            <Tooltip title={t('artwork_drive.open_in_drive')}>
              <IconButton
                size="small"
                component="a"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('artwork_drive.open_in_drive')}
                sx={iconSx}
              >
                <LinkExternalIcon width={15} height={15} />
              </IconButton>
            </Tooltip>
          ) : null}
          {showUpload || menuActions.length ? (
            <Tooltip title={t('artwork_drive.more_actions')}>
              <IconButton
                size="small"
                aria-label={t('artwork_drive.more_actions')}
                aria-haspopup="true"
                aria-expanded={Boolean(menuAnchor)}
                onClick={openMenu}
                onMouseEnter={showUpload ? preload : undefined}
                onFocus={showUpload ? preload : undefined}
                sx={iconSx}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      ) : (
        <Tooltip title={lastSynced}>
          <span>
            <DriveFolderPill
              files={summary?.files ?? 0}
              links={summary?.links ?? 0}
              hasMenu={opensMenu}
              open={Boolean(menuAnchor)}
              loading={uploading}
              onClick={openMenu}
              onMouseEnter={showUpload ? preload : undefined}
              onFocus={showUpload ? preload : undefined}
            />
          </span>
        </Tooltip>
      )}
      <DriveFolderMenu
        anchorEl={menuAnchor}
        onClose={closeMenu}
        link={presentation === 'icons' ? undefined : link}
        onUpload={showUpload ? () => upload(folder, title) : undefined}
        uploading={uploading}
        actions={menuActions}
      />
      {signInNeeded ? (
        <GoogleSignInDialog onContinue={continueWithGoogle} onCancel={dismissSignIn} />
      ) : null}
      {accessOpen ? (
        <DriveAccessDialog
          open={accessOpen}
          onClose={() => setAccessOpen(false)}
          orderId={orderId}
          folder={folder}
          title={title}
        />
      ) : null}
    </Stack>
  );
};

export default DriveFolderStatus;
